import { RyeconClock } from '@dbt-labs/sourdough';

import DetailTabs, { TabInfo } from '@/components/DetailTabs';
import { useSemanticModel } from '@/hooks/dbtStrategy/useSemanticModel';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import {
  formatAbsoluteLocalDate,
  formatDateCommon,
} from '../../../../../../../util/date';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { DimensionsView } from '../Tabs/DimensionsView';
import { GeneralView } from '../Tabs/GeneralView';
import { MeasuresView } from '../Tabs/MeasuresView';

const tabs: TabInfo[] = [
  { type: 'general' },
  { type: 'dimensions' },
  { type: 'measures' },
];

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const SemanticModelView = ({ uniqueId }: ResourceParams) => {
  const semanticModelQuery = useSemanticModel(uniqueId);

  if (semanticModelQuery.error) return <>{semanticModelQuery.error.stack}</>;
  const semanticModel =
    (semanticModelQuery.fetching ? null : semanticModelQuery.data) ?? null;

  const lastUpdatedAtUnformatted = semanticModel?.definition?.runGeneratedAt;
  const date = lastUpdatedAtUnformatted
    ? new Date(lastUpdatedAtUnformatted)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;

  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('semantic_model')];
  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Definition updated as of ${updatedAt}`,
      tooltip: updatedAtUtc || undefined,
    });
  }

  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={semanticModel?.definition?.name ?? uniqueId.remainder}
        headerIcons={headerIcons}
        packageName={uniqueId.packageName}
        files={[
          semanticModel?.definition?.patchPath,
          semanticModel?.definition?.filePath,
        ]}
        name={semanticModel?.definition?.name}
        resourceType="semantic_model"
        updatedDate={date}
      />
      <DetailTabs tabs={tabs} show={!!semanticModel}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <>
                  <GeneralView
                    resource={semanticModel?.definition as GenericResourceNode}
                    uniqueId={uniqueId}
                    description={semanticModel?.definition?.description}
                    meta={null}
                  />
                </>
              )}
              {activeTab === 'dimensions' && (
                <DimensionsView resource={semanticModel} />
              )}
              {activeTab === 'measures' && <MeasuresView resource={semanticModel} />}
            </>
          );
        }}
      </DetailTabs>
    </>
  );
};
